import * as React from "react";

import { api } from "@repo/backend";
import type { Id } from "@repo/backend/convex/_generated/dataModel";
import { getResponsiveImageSources } from "@repo/shared/images/vercel-image";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@repo/ui/components/context-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@repo/ui/components/empty";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { ScrollArea } from "@repo/ui/components/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@repo/ui/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@repo/ui/components/sheet";
import { Textarea } from "@repo/ui/components/textarea";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAction, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import {
  ExternalLink,
  FilePenLineIcon,
  ImageIcon,
  PencilIcon,
  PlusIcon,
  Search,
  Trash2Icon,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { getErrorMessage } from "@/lib/utils";

const POSE_IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/avif";
const MAX_POSE_IMAGE_BYTES = 20 * 1024 * 1024;
const POSE_GRID_IMAGE_SIZES = "(max-width: 768px) 50vw, (max-width: 1280px) 25vw, 320px";

type Category = (typeof api.poses.listCategories)["_returnType"][number];
type PoseEntry = (typeof api.poses.listAvailable)["_returnType"]["page"][number];

type PoseAssetUploadTarget = { uploadUrl: string; publicUrl: string };

const validatePoseImageFile = (file: File) => {
  if (!POSE_IMAGE_ACCEPT.split(",").includes(file.type)) {
    return "Upload a PNG, JPEG, WebP, or AVIF image.";
  }

  if (file.size > MAX_POSE_IMAGE_BYTES) {
    return "Pose image must be 20MB or smaller.";
  }

  return null;
};

const uploadPoseImage = async (
  file: File,
  getUploadTarget: () => Promise<PoseAssetUploadTarget>,
) => {
  const error = validatePoseImageFile(file);
  if (error) throw new Error(error);

  const target = await getUploadTarget();
  const response = await fetch(target.uploadUrl, {
    body: file,
    headers: { "Content-Type": file.type },
    method: "PUT",
  });

  if (!response.ok) throw new Error("Could not upload the pose image.");

  return target.publicUrl;
};

function OptimizedImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const imageSources = getResponsiveImageSources({
    fallbackWidth: 420,
    quality: 75,
    sizes: POSE_GRID_IMAGE_SIZES,
    src,
  });

  return (
    <img
      alt={alt}
      className={className}
      loading="lazy"
      sizes={imageSources.sizes}
      src={imageSources.src}
      srcSet={imageSources.srcSet}
    />
  );
}

export const Route = createFileRoute("/_authed/poses/view")({
  head: () => ({ meta: [{ title: "Poses View" }] }),
  component: PosesViewRoute,
  validateSearch: z.object({
    search: z.string().optional().default(""),
    category: z.string().optional().default("all"),
  }),
});

function PosesViewRoute() {
  const { search, category: activeCategoryId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [, startNavigation] = React.useTransition();
  const [searchDraft, setSearchDraft] = React.useState(search);
  const categories = useQuery(api.poses.listCategories);
  const categoryId =
    activeCategoryId === "all" ? undefined : (activeCategoryId as Id<"poseCategories">);
  const listAvailableArgs = React.useMemo(() => ({ search, categoryId }), [categoryId, search]);
  const { results, status, loadMore } = usePaginatedQuery(
    api.poses.listAvailable,
    listAvailableArgs,
    {
      initialNumItems: 30,
    },
  );

  React.useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (status !== "CanLoadMore") return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore(30);
      },
      { rootMargin: "800px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [status, loadMore]);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (searchDraft === search) {
        return;
      }

      startNavigation(() => {
        navigate({
          replace: true,
          search: { search: searchDraft, category: activeCategoryId },
        });
      });
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [activeCategoryId, navigate, search, searchDraft]);

  const totalPoses = React.useMemo(
    () => categories?.reduce((count, category) => count + category.poseCount, 0) ?? 0,
    [categories],
  );
  const categoryOptions = React.useMemo(() => categories ?? [], [categories]);
  const activeCategory = React.useMemo(
    () => categoryOptions.find((category) => String(category._id) === activeCategoryId),
    [activeCategoryId, categoryOptions],
  );
  const isLoadingFirstPage = categories === undefined || status === "LoadingFirstPage";
  const isEmpty = !isLoadingFirstPage && results.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Pose library</h2>
          <p className="text-sm text-muted-foreground">
            Search, category filtering, and uploaded pose images are served from Convex.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <CreatePoseSheet categories={categories ?? []} />
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="h-full min-h-0 lg:sticky lg:top-4">
          <Card className="flex h-full flex-col overflow-hidden lg:max-h-[calc(100dvh-9rem)]">
            <CardHeader>
              <CardTitle>Filters</CardTitle>
              <CardDescription>
                Limit the library by category or search by pose name.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="Search poses"
                  value={searchDraft}
                />
              </div>

              <ScrollArea className="min-h-0 flex-1 pr-2">
                <div className="flex flex-col gap-2">
                  <Button
                    className="justify-between"
                    onClick={() =>
                      navigate({ replace: true, search: { search: searchDraft, category: "all" } })
                    }
                    variant={activeCategoryId === "all" ? "secondary" : "ghost"}
                  >
                    All categories
                    <Badge variant="outline">{totalPoses}</Badge>
                  </Button>

                  {categoryOptions.map((category) => (
                    <CategoryFilterItem
                      key={String(category._id)}
                      activeCategoryId={activeCategoryId}
                      category={category}
                      onSelect={(nextCategoryId) =>
                        navigate({
                          replace: true,
                          search: { search: searchDraft, category: nextCategoryId },
                        })
                      }
                      onDeleted={() => {
                        if (activeCategoryId === String(category._id)) {
                          navigate({
                            replace: true,
                            search: { search: searchDraft, category: "all" },
                          });
                        }
                      }}
                    />
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
            <CardFooter>
              <CreateCategoryDialog />
            </CardFooter>
          </Card>
        </div>

        <div className="flex h-full min-h-0 flex-col overflow-hidden lg:max-h-[calc(100dvh-9rem)]">
          <ScrollArea className="min-h-0 flex-1">
            <div className="flex flex-col gap-4 pr-2">
              {isLoadingFirstPage ? (
                <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div className="aspect-3/4 rounded-xl border bg-muted/20" key={index} />
                  ))}
                </div>
              ) : null}

              {isEmpty ? (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <ImageIcon className="size-4" />
                    </EmptyMedia>
                    <EmptyTitle>No poses found</EmptyTitle>
                    <EmptyDescription>
                      {activeCategory && !search
                        ? `Category ${activeCategory.title} does not have any poses yet.`
                        : "Try another search term or switch back to all categories."}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : null}

              {results.length > 0 ? (
                <>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
                    {results.map((pose) => (
                      <PoseGridItem
                        key={String(pose._id)}
                        pose={pose}
                        categories={categories ?? []}
                        listAvailableArgs={listAvailableArgs}
                      />
                    ))}
                  </div>

                  <div ref={sentinelRef} />
                  {status === "CanLoadMore" ? (
                    <div className="flex justify-center py-4">
                      <Button onClick={() => loadMore(30)} variant="outline">
                        Load more
                      </Button>
                    </div>
                  ) : null}
                  {status === "LoadingMore" ? (
                    <div className="flex justify-center py-4">
                      <span className="text-sm text-muted-foreground">Loading more...</span>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

function CategoryFilterItem({
  category,
  activeCategoryId,
  onSelect,
  onDeleted,
}: {
  category: Category;
  activeCategoryId: string;
  onSelect: (categoryId: string) => void;
  onDeleted: () => void;
}) {
  const confirm = useConfirmationDialog();
  const deleteCategory = useMutation(api.poses.deleteCategory).withOptimisticUpdate(
    (localStore, args) => {
      const categories = localStore.getQuery(api.poses.listCategories, {});
      if (!categories) return;

      localStore.setQuery(
        api.poses.listCategories,
        {},
        categories.filter((item) => item._id !== args.categoryId),
      );
    },
  );
  const [isEditOpen, setIsEditOpen] = React.useState(false);

  const handleDelete = React.useCallback(async () => {
    const allowed = await confirm({
      title: "Delete category",
      description: `Delete "${category.title}"? This only works when the category is unused.`,
      confirmText: "Delete",
    });

    if (!allowed) return;

    try {
      await deleteCategory({ categoryId: category._id });
      onDeleted();
      toast.success("Category deleted.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete the category."));
    }
  }, [category._id, category.title, confirm, deleteCategory, onDeleted]);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger className="flex w-full">
          <Button
            className="w-full justify-between"
            onClick={() => onSelect(String(category._id))}
            variant={activeCategoryId === String(category._id) ? "secondary" : "ghost"}
          >
            <span className="truncate">{category.title}</span>
            <Badge variant="outline">{category.poseCount}</Badge>
          </Button>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={() => setIsEditOpen(true)}>
            <PencilIcon className="size-4" />
            Edit category
          </ContextMenuItem>
          <ContextMenuItem onClick={() => void handleDelete()} variant="destructive">
            <Trash2Icon className="size-4" />
            Delete category
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      <CategoryDialog category={category} open={isEditOpen} onOpenChange={setIsEditOpen} />
    </>
  );
}

function CreateCategoryDialog() {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button className="w-full gap-2" onClick={() => setOpen(true)} variant="outline">
        <PlusIcon className="size-4" />
        Add category
      </Button>
      <CategoryDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function CategoryDialog({
  category,
  open,
  onOpenChange,
}: {
  category?: Category;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createCategory = useMutation(api.poses.createCategory);
  const updateCategory = useMutation(api.poses.updateCategory).withOptimisticUpdate(
    (localStore, args) => {
      const categories = localStore.getQuery(api.poses.listCategories, {});
      const nextInstruction = args.instruction?.trim() || undefined;
      if (!categories) return;

      localStore.setQuery(
        api.poses.listCategories,
        {},
        categories
          .map((item) =>
            item._id === args.categoryId
              ? { ...item, title: args.title.trim(), instruction: nextInstruction }
              : item,
          )
          .toSorted((left, right) => left.title.localeCompare(right.title)),
      );
    },
  );
  const form = useForm({
    defaultValues: {
      title: category?.title ?? "",
      instruction: category?.instruction ?? "",
    },
    onSubmit: async ({ value }) => {
      const title = value.title.trim();
      if (!title) {
        toast.error("Category title is required.");
        return;
      }

      try {
        if (category) {
          await updateCategory({
            categoryId: category._id,
            title,
            instruction: value.instruction.trim() || undefined,
          });
          toast.success("Category updated.");
        } else {
          await createCategory({ title, instruction: value.instruction.trim() || undefined });
          toast.success("Category created.");
        }

        onOpenChange(false);
      } catch (error) {
        toast.error(getErrorMessage(error, "Could not save the category."));
      }
    },
  });

  React.useEffect(() => {
    if (!open) return;
    form.reset({
      title: category?.title ?? "",
      instruction: category?.instruction ?? "",
    });
  }, [category, form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>{category ? "Edit category" : "Add category"}</DialogTitle>
            <DialogDescription>
              {category
                ? "Rename the category used for pose grouping."
                : "Create a new category for organizing poses."}
            </DialogDescription>
          </DialogHeader>

          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel
                  htmlFor={category ? `category-title-${String(category._id)}` : "category-title"}
                >
                  Title
                </FieldLabel>
                <form.Field name="title">
                  {(field) => (
                    <Input
                      id={category ? `category-title-${String(category._id)}` : "category-title"}
                      autoComplete="off"
                      placeholder="Winner Pose"
                      value={field.state.value}
                      onChange={(event) => field.handleChange(event.target.value)}
                    />
                  )}
                </form.Field>
              </Field>

              <Field>
                <FieldLabel
                  htmlFor={
                    category
                      ? `category-instruction-${String(category._id)}`
                      : "category-instruction"
                  }
                >
                  Instruction
                </FieldLabel>
                <form.Field name="instruction">
                  {(field) => (
                    <Textarea
                      id={
                        category
                          ? `category-instruction-${String(category._id)}`
                          : "category-instruction"
                      }
                      placeholder="Choose a confident, celebratory pose that feels like a winner moment."
                      value={field.state.value}
                      onChange={(event) => field.handleChange(event.target.value)}
                    />
                  )}
                </form.Field>
                <FieldDescription>Shown behind the info button in the posing app.</FieldDescription>
              </Field>
            </FieldGroup>
          </FieldSet>

          <DialogFooter>
            <Button type="submit">{category ? "Save changes" : "Create category"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PoseGridItem({
  pose,
  categories,
  listAvailableArgs,
}: {
  pose: PoseEntry;
  categories: Category[];
  listAvailableArgs: { search: string; categoryId: Id<"poseCategories"> | undefined };
}) {
  const confirm = useConfirmationDialog();
  const deletePose = useMutation(api.poses.deletePose).withOptimisticUpdate((localStore, args) => {
    for (const query of localStore.getAllQueries(api.poses.listAvailable)) {
      if (!query.value || query.args.search !== listAvailableArgs.search) continue;
      if (query.args.categoryId !== listAvailableArgs.categoryId) continue;

      localStore.setQuery(api.poses.listAvailable, query.args, {
        ...query.value,
        page: query.value.page.filter((item) => item._id !== args.poseId),
      });
    }
  });
  const [isEditOpen, setIsEditOpen] = React.useState(false);

  const handleDelete = React.useCallback(async () => {
    const allowed = await confirm({
      title: "Delete pose",
      description: `Delete "${pose.title}"? This action cannot be undone.`,
      confirmText: "Delete",
    });

    if (!allowed) return;

    try {
      await deletePose({ poseId: pose._id });
      toast.success("Pose deleted.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete the pose."));
    }
  }, [confirm, deletePose, pose._id, pose.title]);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger className="flex w-full">
          <article className="w-full overflow-hidden rounded-xl border bg-card">
            <div className="aspect-3/4 overflow-hidden bg-muted">
              <OptimizedImage
                alt={pose.title}
                className="size-full object-cover object-top"
                src={pose.imageUrl ?? ""}
              />
            </div>
            <div className="flex flex-col gap-3 p-3">
              <div className="space-y-1">
                <h3 className="font-medium">{pose.title}</h3>
                <p className="text-xs text-muted-foreground">{pose.categoryTitle}</p>
              </div>

              {pose.videoUrl ? (
                <a
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  href={pose.videoUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Preview motion
                  <ExternalLink className="size-4" />
                </a>
              ) : (
                <span className="text-sm text-muted-foreground">No video reference</span>
              )}
            </div>
          </article>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={() => setIsEditOpen(true)}>
            <FilePenLineIcon className="size-4" />
            Edit pose
          </ContextMenuItem>
          <ContextMenuItem onClick={() => void handleDelete()} variant="destructive">
            <Trash2Icon className="size-4" />
            Delete pose
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      <EditPoseSheet
        pose={pose}
        categories={categories}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
    </>
  );
}

function CreatePoseSheet({ categories }: { categories: Category[] }) {
  const createPose = useMutation(api.poses.createPose);
  const getUploadTarget = useAction(api.poses.generatePoseAssetUploadUrl);
  const [open, setOpen] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [inputKey, setInputKey] = React.useState(0);
  const canSubmit = categories.length > 0 && !isSubmitting;
  const categoryTitleById = React.useMemo(
    () => new Map(categories.map((category) => [String(category._id), category.title])),
    [categories],
  );

  const clearSelectedImage = React.useCallback(() => {
    setSelectedImage(null);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setInputKey((current) => current + 1);
  }, []);

  const handleImageChange = React.useCallback(
    (file: File | null) => {
      clearSelectedImage();
      if (!file) return;

      const error = validatePoseImageFile(file);
      if (error) {
        toast.error(error);
        return;
      }

      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [clearSelectedImage],
  );

  const form = useForm({
    defaultValues: {
      title: "",
      categoryId: "",
      videoUrl: "",
    },
    onSubmit: async ({ value }) => {
      const title = value.title.trim();

      if (!title || !value.categoryId || !selectedImage) {
        toast.error("Title, category, and image are required.");
        return;
      }

      setIsSubmitting(true);

      try {
        const imageUrl = await uploadPoseImage(selectedImage, () =>
          getUploadTarget({
            contentType: selectedImage.type,
            kind: "image",
            sizeBytes: selectedImage.size,
          }),
        );
        await createPose({
          title,
          categoryId: value.categoryId as Id<"poseCategories">,
          imageUrl,
          videoUrl: value.videoUrl.trim() || undefined,
        });

        form.reset();
        clearSelectedImage();
        setOpen(false);
        toast.success("Pose created.");
      } catch (error) {
        toast.error(getErrorMessage(error, "Could not create the pose."));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) return;

      form.reset();
      clearSelectedImage();
      setIsSubmitting(false);
    },
    [clearSelectedImage, form],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger
        render={(props) => (
          <Button {...props} className="gap-2">
            <PlusIcon className="size-4" />
            Add pose
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent>
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Add pose</SheetTitle>
            <SheetDescription>
              Create a single pose and attach it to an existing category.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid gap-5 px-4">
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="pose-title">Title</FieldLabel>
                    <form.Field name="title">
                      {(field) => (
                        <Input
                          id="pose-title"
                          autoComplete="off"
                          placeholder="Hands Up"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="pose-category">Category</FieldLabel>
                    <form.Field name="categoryId">
                      {(field) => (
                        <Select
                          value={field.state.value}
                          onValueChange={(value) => field.handleChange(value ?? "")}
                        >
                          <SelectTrigger className="w-full" id="pose-category">
                            <span
                              className={
                                field.state.value
                                  ? "flex flex-1 text-left"
                                  : "flex flex-1 text-left text-muted-foreground"
                              }
                            >
                              {field.state.value
                                ? (categoryTitleById.get(field.state.value) ?? field.state.value)
                                : "Select category"}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={String(category._id)} value={String(category._id)}>
                                {category.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                    {!categories.length ? (
                      <FieldDescription>
                        Create a category first before adding poses.
                      </FieldDescription>
                    ) : null}
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="pose-image-file">Image</FieldLabel>
                    <Input
                      id="pose-image-file"
                      key={inputKey}
                      type="file"
                      accept={POSE_IMAGE_ACCEPT}
                      onChange={(event) => handleImageChange(event.target.files?.[0] ?? null)}
                    />
                    <FieldDescription>PNG, JPEG, WebP, or AVIF up to 20MB.</FieldDescription>
                    {previewUrl ? (
                      <div className="overflow-hidden rounded-lg border bg-muted">
                        <img
                          alt="Selected pose preview"
                          className="aspect-3/4 w-full object-cover object-top"
                          src={previewUrl}
                        />
                      </div>
                    ) : null}
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="pose-video-url">Video URL</FieldLabel>
                    <form.Field name="videoUrl">
                      {(field) => (
                        <Input
                          id="pose-video-url"
                          autoComplete="off"
                          placeholder="https://example.com/hands-up.mp4"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>

            <div className="border-t px-4 pt-4">
              <Button className="w-full" type="submit" disabled={!canSubmit || !selectedImage}>
                {isSubmitting ? "Creating..." : "Create pose"}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EditPoseSheet({
  pose,
  categories,
  open,
  onOpenChange,
}: {
  pose: PoseEntry;
  categories: Category[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updatePose = useMutation(api.poses.updatePose);
  const getUploadTarget = useAction(api.poses.generatePoseAssetUploadUrl);
  const [selectedImage, setSelectedImage] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [inputKey, setInputKey] = React.useState(0);
  const canSubmit = categories.length > 0 && !isSubmitting;
  const categoryTitleById = React.useMemo(
    () => new Map(categories.map((category) => [String(category._id), category.title])),
    [categories],
  );

  const clearSelectedImage = React.useCallback(() => {
    setSelectedImage(null);
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    setInputKey((current) => current + 1);
  }, []);

  const handleImageChange = React.useCallback(
    (file: File | null) => {
      clearSelectedImage();
      if (!file) return;

      const error = validatePoseImageFile(file);
      if (error) {
        toast.error(error);
        return;
      }

      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [clearSelectedImage],
  );

  const form = useForm({
    defaultValues: {
      title: pose.title,
      categoryId: String(pose.categoryId),
      videoUrl: pose.videoUrl ?? "",
    },
    onSubmit: async ({ value }) => {
      const title = value.title.trim();

      if (!title || !value.categoryId) {
        toast.error("Title and category are required.");
        return;
      }

      setIsSubmitting(true);

      try {
        const imageUrl = selectedImage
          ? await uploadPoseImage(selectedImage, () =>
              getUploadTarget({
                contentType: selectedImage.type,
                kind: "image",
                sizeBytes: selectedImage.size,
              }),
            )
          : undefined;

        await updatePose({
          poseId: pose._id,
          title,
          categoryId: value.categoryId as Id<"poseCategories">,
          imageUrl,
          videoUrl: value.videoUrl.trim() || undefined,
        });

        clearSelectedImage();
        onOpenChange(false);
        toast.success("Pose updated.");
      } catch (error) {
        toast.error(getErrorMessage(error, "Could not update the pose."));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    if (!open) return;
    form.reset({
      title: pose.title,
      categoryId: String(pose.categoryId),
      videoUrl: pose.videoUrl ?? "",
    });
    clearSelectedImage();
  }, [clearSelectedImage, form, open, pose]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (nextOpen) return;

      clearSelectedImage();
      setIsSubmitting(false);
    },
    [clearSelectedImage, onOpenChange],
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent>
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Edit pose</SheetTitle>
            <SheetDescription>
              Update the pose details and move it to another category if needed.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid gap-5 px-4">
              <FieldSet>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor={`pose-title-${String(pose._id)}`}>Title</FieldLabel>
                    <form.Field name="title">
                      {(field) => (
                        <Input
                          id={`pose-title-${String(pose._id)}`}
                          autoComplete="off"
                          placeholder="Hands Up"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>

                  <Field>
                    <FieldLabel htmlFor={`pose-category-${String(pose._id)}`}>Category</FieldLabel>
                    <form.Field name="categoryId">
                      {(field) => (
                        <Select
                          value={field.state.value}
                          onValueChange={(value) => field.handleChange(value ?? "")}
                        >
                          <SelectTrigger
                            className="w-full"
                            id={`pose-category-${String(pose._id)}`}
                          >
                            <span
                              className={
                                field.state.value
                                  ? "flex flex-1 text-left"
                                  : "flex flex-1 text-left text-muted-foreground"
                              }
                            >
                              {field.state.value
                                ? (categoryTitleById.get(field.state.value) ?? field.state.value)
                                : "Select category"}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={String(category._id)} value={String(category._id)}>
                                {category.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                    {!categories.length ? (
                      <FieldDescription>
                        Create a category first before editing poses.
                      </FieldDescription>
                    ) : null}
                  </Field>

                  <Field>
                    <FieldLabel htmlFor={`pose-image-file-${String(pose._id)}`}>Image</FieldLabel>
                    <div className="overflow-hidden rounded-lg border bg-muted">
                      <OptimizedImage
                        alt={pose.title}
                        className="aspect-3/4 w-full object-cover object-top"
                        src={previewUrl ?? pose.imageUrl ?? ""}
                      />
                    </div>
                    <Input
                      id={`pose-image-file-${String(pose._id)}`}
                      key={inputKey}
                      type="file"
                      accept={POSE_IMAGE_ACCEPT}
                      onChange={(event) => handleImageChange(event.target.files?.[0] ?? null)}
                    />
                    <FieldDescription>
                      Choose a new image only if you want to replace it.
                    </FieldDescription>
                  </Field>

                  <Field>
                    <FieldLabel htmlFor={`pose-video-url-${String(pose._id)}`}>
                      Video URL
                    </FieldLabel>
                    <form.Field name="videoUrl">
                      {(field) => (
                        <Input
                          id={`pose-video-url-${String(pose._id)}`}
                          autoComplete="off"
                          placeholder="https://example.com/hands-up.mp4"
                          value={field.state.value}
                          onChange={(event) => field.handleChange(event.target.value)}
                        />
                      )}
                    </form.Field>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>

            <div className="border-t px-4 pt-4">
              <Button className="w-full" type="submit" disabled={!canSubmit}>
                {isSubmitting ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
